<script>
  import { useUrlParams } from "@stores/urlStore.js";
  import { fetchWithSession } from "lib/util/fetchWithSession.js";
  import { projectListStore } from "@stores/damProjectsStore.js";
  import { useNavigate } from "svelte-navigator";
  import { ROUTES } from "@utils/routes.js";
  import { Modal as DangerModal } from "carbon-components-svelte";
  import {
    InfoBlock,
    TextArea,
    SectionWrapper,
    Button,
  } from "paxray-components-svelte";
  import Header from "@components/Header.svelte";
  import ProjectStepActions from "../components/ProjectStepActions.svelte";
  import { fly } from "svelte/transition";
  import { PROJECT_STAGES } from "@utils/stagesAndSteps.js";
  import PhaseMessageLanguage from "../components/PhaseMessageLanguage.svelte";
  import { START_DATA_COLLECTION_MESSAGES } from "./phaseMessages.js";

  const { markStepAsCompleted } = projectListStore;
  const urlParams = useUrlParams();
  const navigate = useNavigate();
  const step = PROJECT_STAGES.DATA_COLLECTION.steps.START_DATA_COLL;
  const stage = PROJECT_STAGES.DATA_COLLECTION.id;

  let language = "en";
  let message = START_DATA_COLLECTION_MESSAGES.en;
  let openDangerModal = false;
  let editingMessage = false;
  let stepActionsRef;

  $: ({ idproject } = $urlParams);
  $: ({ currentProjectContacts } = $projectListStore);
  $: projectProgressHref = `${ROUTES.dam.progress}?${new URLSearchParams({
    idproject,
  }).toString()}`;

  async function startDataCollection() {
    console.log("Starting data collection");
    const res = await fetchWithSession(`/api/damprojects/startMainRecording`, {
      method: "POST",
      body: JSON.stringify({
        idproject: idproject,
        message: message,
      }),
    });
    const data = await res.json();
    if (data?.ok) {
      await handleMarkAsCompleted();
    }

    openDangerModal = false;
  }

  async function handleMarkAsCompleted() {
    const data = await markStepAsCompleted(idproject, step, stage);
    if (data?.ok) {
      stepActionsRef?.refresh();
      navigate(`${ROUTES.dam.progress}?idproject=${idproject}`);
    }
  }
</script>

<Header
  section="Project"
  area="Discovery Extended"
  breadcrumb="Start Data Collection"
  areaHref={projectProgressHref}
  tooltipText="Start the main data collection and show participants the message below. Starting cannot be undone."
>
  <ProjectStepActions
    bind:this={stepActionsRef}
    {idproject}
    {step}
    {stage}
    showGoBackButton={false}
    text="Start Data Collection"
    confirmBeforeComplete={false}
    onClick={() => (openDangerModal = true)}
  />
</Header>

<SectionWrapper fullscreen={true} seamless={true} seamlessPadding={true}>
  <div class="main" slot="main">
    <InfoBlock
      type="info"
      heading="Start Data Collection"
      message="Review the participant message, then start the main data collection."
      showClose={false}
    />
    <div class="row">
      <div class="cell">
        <div class="field_label">
          <PhaseMessageLanguage
            messages={START_DATA_COLLECTION_MESSAGES}
            bind:language
            bind:value={message}
          />
          <Button
            size="field"
            kind="secondary"
            on:click={() => (editingMessage = !editingMessage)}
          >
            {editingMessage ? "Done editing" : "Edit message"}
          </Button>
        </div>
        {#if editingMessage}
          <TextArea
            placeholder="Enter a message for the users"
            persons={currentProjectContacts}
            bind:value={message}
            richText
            labelText="User Message"
          />
        {:else}
          <div class="message-preview">
            {@html message}
          </div>
        {/if}
      </div>
    </div>
  </div></SectionWrapper
>

<DangerModal
  danger
  bind:open={openDangerModal}
  preventCloseOnClickOutside
  modalHeading="Start Data Collection?"
  primaryButtonText="Start Data Collection"
  secondaryButtonText="Cancel"
  on:click:button--primary={startDataCollection}
  on:click:button--secondary={() => (openDangerModal = false)}
>
  <p class="deleteModalBody">
    Start the main data collection and show the participant message. This action
    cannot be undone.
  </p>
</DangerModal>

<style>
  .field_label {
    display: flex;
    align-items: flex-end;
    gap: var(--xs);
    margin-bottom: var(--control-gap);
  }

  .field_label :global(.phase_message_language) {
    margin-left: 0;
  }

  .message-preview {
    padding: 1rem;
    border: 1px solid var(--border-color);
    border-radius: 0.25rem;
    overflow-wrap: anywhere;
  }

  .message-preview :global(b),
  .message-preview :global(strong) {
    font-weight: bold;
  }

  .message-preview :global(i),
  .message-preview :global(em) {
    font-style: italic;
  }

  .main {
    max-width: 100%;
    width: 100%;
    height: 100%;
    min-height: 0;
    display: flex;
    flex-direction: column;
    gap: var(--control-gap);
  }

  .main :global(.warning-notice) {
    margin-bottom: 0;
  }

  .row {
    display: flex;
    flex-direction: row;
    gap: 2rem;
    width: 100%;
    flex: 1 1 auto;
    min-height: 0;
  }
  .cell {
    flex: 1 1 auto;
    width: 50%;
    height: 100%;
    min-height: 0;
    display: flex;
    flex-direction: column;
  }
  .cell :global(.richtext) {
    flex: 0 0 50vh;
    height: 50vh;
    min-height: 0;
    display: flex;
    flex-direction: column;
  }
  .cell :global(.richtext .content) {
    flex: 1 1 auto;
    min-height: 0;
    overflow: auto;
  }
</style>
